import dayjs from "dayjs";
import { useSelector } from "react-redux";
import { Button, Grid, IconButton, Paper, Stack, Typography } from "@mui/material";
import { Plus, Trash2 } from "lucide-react";

import { RootState } from "@store/store";
import { CustomAccordion } from "@components/CustomAccordion";
import { formatCurrency, formatDateWithWeekday } from "@helpers/formats";

import { useParametricosContext } from "../../context/ParametricosContext";
import { DateInput, FieldRow, HectareInput, MoneyInput, ReadonlyValue, TextInput } from "../fields";

export default function DatosParametricos() {
    const applicationSelected = useSelector((state: RootState) => state.components.applicationSelected);
    const {
        permissionData,
        evaluationDate,
        evaluatorName,
        entityFederativaName,
        creditorName,
        sowingTypeName,
        hectares,
        yieldPerHectare,
        pricePerTon,
        creditQuotaPerHectare,
        firstMinistrationDate,
        secondMinistrationDate,
        dataForm,
        creditAmount,
        financingAmount,
        contractMaturityDate,
        amortizationSchedule,
        marginRate,
        interestType,
        contractedRate,
        creditTermMonths,
        creditMaturityDate,
        inversionTotalDelProyecto,
        capacidadDePago,
        liquidez,
        apalancamiento,
        save,
    } = useParametricosContext();

    const disabled = permissionData?.can_edit === false;

    const investmentConceptSuggestion = `ESTABLECIMIENTO Y PRODUCCIÓN DE ${hectares} hectareas de ${sowingTypeName} en ${entityFederativaName}`;

    const totalLiabilities = dataForm.values.liabilities.shortTerm + dataForm.values.liabilities.longTerm;
    const totalFixedAssets = dataForm.values.assets.movable + dataForm.values.assets.immovable;
    const totalAmortizationCapital = amortizationSchedule.reduce((sum, row) => sum + row.capital, 0);
    const totalAmortizationInterest = amortizationSchedule.reduce((sum, row) => sum + row.interest, 0);

    return (
        <Stack sx={{ rowGap: 2, py: 2 }}>
            <Paper variant="section">
                <CustomAccordion title={<Typography sx={{ fontWeight: 700 }}>I. Datos Generales</Typography>} defaultExpanded>
                    <Stack spacing={2}>
                        <FieldRow label="Fecha Evaluación">
                            <ReadonlyValue value={formatDateWithWeekday(evaluationDate)} />
                        </FieldRow>
                        <FieldRow label="Entidad Federativa">
                            <ReadonlyValue value={entityFederativaName || "--"} />
                        </FieldRow>
                        <FieldRow label="Nombre del Acreditado">
                            <ReadonlyValue value={creditorName || "--"} />
                        </FieldRow>
                        <FieldRow label="Clasificación del Cliente">
                            <ReadonlyValue value="--" />
                        </FieldRow>
                        <FieldRow label="Grado de Riesgo (P.I.)">
                            <ReadonlyValue value="--" />
                        </FieldRow>
                    </Stack>
                </CustomAccordion>
            </Paper>

            <Paper variant="section">
                <CustomAccordion title={<Typography sx={{ fontWeight: 700 }}>II. Actividad Productiva</Typography>} defaultExpanded>
                    <Stack spacing={2}>
                        <Grid container spacing={2}>
                            <Grid size={2}>
                                <Typography variant="body2" sx={{ fontWeight: 700, opacity: 0.55 }}>
                                    Cultivo
                                </Typography>
                                <ReadonlyValue value={sowingTypeName || "--"} />
                            </Grid>
                            <Grid size={2}>
                                <Typography variant="body2" sx={{ fontWeight: 700, opacity: 0.55 }}>
                                    Superficie que Siembra (ha)
                                </Typography>
                                <HectareInput
                                    value={hectares}
                                    onChange={(value) => dataForm.setFieldValue("hectaresOverride", value)}
                                    disabled={disabled}
                                    decimalScale={2}
                                />
                            </Grid>
                            <Grid size={2}>
                                <Typography variant="body2" sx={{ fontWeight: 700, opacity: 0.55 }}>
                                    Rend (T/ha)
                                </Typography>
                                <ReadonlyValue value={yieldPerHectare} />
                            </Grid>
                            <Grid size={2}>
                                <Typography variant="body2" sx={{ fontWeight: 700, opacity: 0.55 }}>
                                    Precio Venta ($/ha)
                                </Typography>
                                <ReadonlyValue value={formatCurrency(pricePerTon)} />
                            </Grid>
                            <Grid size={2}>
                                <Typography variant="body2" sx={{ fontWeight: 700, opacity: 0.55 }}>
                                    Cuota/ha
                                </Typography>
                                <ReadonlyValue value={formatCurrency(creditQuotaPerHectare)} />
                            </Grid>
                            <Grid size={2}>
                                <Typography variant="body2" sx={{ fontWeight: 700, opacity: 0.55 }}>
                                    Superficie para CREDITO (ha)
                                </Typography>
                                <HectareInput
                                    value={dataForm.values.creditSurfaceHa}
                                    onChange={(value) => dataForm.setFieldValue("creditSurfaceHa", value)}
                                    disabled={disabled}
                                    decimalScale={2}
                                />
                            </Grid>
                            <Grid size={2}>
                                <Typography variant="body2" sx={{ fontWeight: 700, opacity: 0.55 }}>
                                    Monto de Crédito
                                </Typography>
                                <ReadonlyValue value={formatCurrency(creditAmount)} />
                            </Grid>
                        </Grid>

                        <FieldRow label="Concepto de Inversión (CT)">
                            <Stack spacing={0.5}>
                                <TextInput
                                    value={dataForm.values.investmentConceptCt}
                                    onChange={(value) => dataForm.setFieldValue("investmentConceptCt", value)}
                                    disabled={disabled}
                                />
                                {!dataForm.values.investmentConceptCt && (
                                    <Button
                                        size="small"
                                        variant="text"
                                        sx={{ alignSelf: "flex-start" }}
                                        onClick={() => dataForm.setFieldValue("investmentConceptCt", investmentConceptSuggestion)}
                                        disabled={disabled}
                                    >
                                        Usar sugerencia: {investmentConceptSuggestion}
                                    </Button>
                                )}
                            </Stack>
                        </FieldRow>
                        <FieldRow label="Concepto de Inversión (Refa)">
                            <TextInput
                                value={dataForm.values.investmentConceptRefa}
                                onChange={(value) => dataForm.setFieldValue("investmentConceptRefa", value)}
                                disabled={disabled}
                            />
                        </FieldRow>
                        <FieldRow label="Programa">
                            <TextInput
                                value={dataForm.values.program}
                                onChange={(value) => dataForm.setFieldValue("program", value)}
                                disabled={disabled}
                            />
                        </FieldRow>
                        <FieldRow label="Producto">
                            <ReadonlyValue value={applicationSelected.tax_system?.name ?? "--"} />
                        </FieldRow>
                        <FieldRow label="Fuente de Fondeo">
                            <TextInput
                                value={dataForm.values.fundingSource}
                                onChange={(value) => dataForm.setFieldValue("fundingSource", value)}
                                disabled={disabled}
                            />
                        </FieldRow>
                        <FieldRow label="Inversión Total del Proyecto">
                            <ReadonlyValue value={formatCurrency(inversionTotalDelProyecto)} />
                        </FieldRow>
                        <FieldRow label="Crédito Autorizado">
                            <ReadonlyValue value={formatCurrency(creditAmount)} />
                        </FieldRow>
                        <FieldRow label="Fecha de Vencimiento del Crédito">
                            <ReadonlyValue value={creditMaturityDate ? dayjs(creditMaturityDate).format("DD/MM/YYYY") : "--"} />
                        </FieldRow>
                        <FieldRow label="Plazo de Crédito (En Meses)">
                            <ReadonlyValue value={creditTermMonths} />
                        </FieldRow>
                    </Stack>
                </CustomAccordion>
            </Paper>

            <Paper variant="section">
                <CustomAccordion title={<Typography sx={{ fontWeight: 700 }}>Contrato Vigente (Recalificación)</Typography>}>
                    <Stack spacing={2}>
                        <FieldRow label="Clasificación del Contrato de Crédito">
                            <TextInput
                                value={dataForm.values.contract.classification}
                                onChange={(value) => dataForm.setFieldValue("contract.classification", value)}
                                disabled={disabled}
                            />
                        </FieldRow>
                        <FieldRow label="No. Contrato Físico">
                            <TextInput
                                value={dataForm.values.contract.contractNumber}
                                onChange={(value) => dataForm.setFieldValue("contract.contractNumber", value)}
                                disabled={disabled}
                            />
                        </FieldRow>
                        <FieldRow label="Monto de Contrato">
                            <MoneyInput
                                value={dataForm.values.contract.amount}
                                onChange={(value) => dataForm.setFieldValue("contract.amount", value)}
                                disabled={disabled}
                            />
                        </FieldRow>
                        <FieldRow label="Fecha de Apertura">
                            <DateInput
                                value={dataForm.values.contract.openingDate}
                                onChange={(value) => dataForm.setFieldValue("contract.openingDate", value)}
                                disabled={disabled}
                            />
                        </FieldRow>
                        <FieldRow label="Fecha de Vencimiento">
                            <DateInput
                                value={dataForm.values.contract.maturityDateOverride || contractMaturityDate}
                                onChange={(value) => dataForm.setFieldValue("contract.maturityDateOverride", value)}
                                disabled={disabled}
                            />
                        </FieldRow>
                        <FieldRow label="Fecha de 1er Convenio">
                            <ReadonlyValue value={firstMinistrationDate ? dayjs(firstMinistrationDate).format("DD/MM/YYYY") : "--"} />
                        </FieldRow>
                        <FieldRow label="Fecha de 2do Convenio">
                            <ReadonlyValue
                                value={
                                    secondMinistrationDate === "N/A"
                                        ? "N/A"
                                        : dayjs(secondMinistrationDate).format("DD/MM/YYYY")
                                }
                            />
                        </FieldRow>
                    </Stack>
                </CustomAccordion>
            </Paper>

            <Paper variant="section">
                <CustomAccordion title={<Typography sx={{ fontWeight: 700 }}>Refaccionario / Bien a Adquirir</Typography>} defaultExpanded>
                    <Stack spacing={2}>
                        <FieldRow label="Valor del Bien a Adquirir">
                            <MoneyInput
                                value={dataForm.values.assetValue}
                                onChange={(value) => dataForm.setFieldValue("assetValue", value)}
                                disabled={disabled}
                            />
                        </FieldRow>
                        <FieldRow label="Importe a Financiar">
                            <MoneyInput
                                value={dataForm.values.financingAmountOverride ?? financingAmount}
                                onChange={(value) => dataForm.setFieldValue("financingAmountOverride", value)}
                                disabled={disabled}
                            />
                        </FieldRow>

                        <Typography sx={{ fontWeight: 700 }}>Tabla de Amortización</Typography>
                        {dataForm.values.amortizationRows.length === 0 ? (
                            <Typography variant="body2">Sin renglones capturados</Typography>
                        ) : (
                            dataForm.values.amortizationRows.map((row, idx) => (
                                <Grid container spacing={2} key={idx} sx={{ alignItems: "center" }}>
                                    <Grid size={1}>
                                        <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                            {idx + 1}
                                        </Typography>
                                    </Grid>
                                    <Grid size={3}>
                                        <DateInput
                                            value={row.date}
                                            onChange={(value) => dataForm.setFieldValue(`amortizationRows[${idx}].date`, value)}
                                            disabled={disabled}
                                        />
                                    </Grid>
                                    <Grid size={3}>
                                        <MoneyInput
                                            value={row.capital}
                                            onChange={(value) => dataForm.setFieldValue(`amortizationRows[${idx}].capital`, value)}
                                            disabled={disabled}
                                        />
                                    </Grid>
                                    <Grid size={3}>
                                        <ReadonlyValue value={formatCurrency(amortizationSchedule[idx]?.interest ?? 0)} />
                                    </Grid>
                                    <Grid size={2}>
                                        <IconButton
                                            onClick={() =>
                                                dataForm.setFieldValue(
                                                    "amortizationRows",
                                                    dataForm.values.amortizationRows.filter((_, i) => i !== idx),
                                                )
                                            }
                                            disabled={disabled}
                                        >
                                            <Trash2 size={18} />
                                        </IconButton>
                                    </Grid>
                                </Grid>
                            ))
                        )}
                        <Button
                            variant="text"
                            startIcon={<Plus size={16} />}
                            sx={{ alignSelf: "flex-start" }}
                            onClick={() =>
                                dataForm.setFieldValue("amortizationRows", [
                                    ...dataForm.values.amortizationRows,
                                    { date: dayjs().toISOString(), capital: 0 },
                                ])
                            }
                            disabled={disabled}
                        >
                            Agregar Renglón
                        </Button>
                        <FieldRow label="Totales">
                            <Typography sx={{ fontWeight: 700 }}>
                                {formatCurrency(totalAmortizationCapital)} capital / {formatCurrency(totalAmortizationInterest)}{" "}
                                intereses
                            </Typography>
                        </FieldRow>
                    </Stack>
                </CustomAccordion>
            </Paper>

            <Paper variant="section">
                <CustomAccordion
                    title={<Typography sx={{ fontWeight: 700 }}>III. Condiciones de Línea y Situación Financiera</Typography>}
                    defaultExpanded
                >
                    <Stack spacing={2}>
                        <FieldRow label="Tasa Contratada">
                            <ReadonlyValue value={`${contractedRate.toFixed(2)}%`} />
                        </FieldRow>
                        <FieldRow label="Tipo">
                            <ReadonlyValue value={interestType || "--"} />
                        </FieldRow>
                        <FieldRow label="Tasa (Margen SFI | Programas)">
                            <ReadonlyValue value={`${marginRate.toFixed(2)}%`} />
                        </FieldRow>
                        <FieldRow label="Capacidad de Pago">
                            <ReadonlyValue value={capacidadDePago === null ? "--" : capacidadDePago.toFixed(2)} />
                        </FieldRow>
                        <FieldRow label="Liquidez">
                            <ReadonlyValue value={liquidez.toFixed(2)} />
                        </FieldRow>
                        <FieldRow label="Apalancamiento">
                            <ReadonlyValue value={apalancamiento.toFixed(2)} />
                        </FieldRow>

                        <Typography sx={{ fontWeight: 700 }}>Pasivos del Cliente</Typography>
                        <FieldRow label="Corto Plazo">
                            <MoneyInput
                                value={dataForm.values.liabilities.shortTerm}
                                onChange={(value) => dataForm.setFieldValue("liabilities.shortTerm", value)}
                                disabled={disabled}
                            />
                        </FieldRow>
                        <FieldRow label="Largo Plazo">
                            <MoneyInput
                                value={dataForm.values.liabilities.longTerm}
                                onChange={(value) => dataForm.setFieldValue("liabilities.longTerm", value)}
                                disabled={disabled}
                            />
                        </FieldRow>
                        <FieldRow label="Total">
                            <ReadonlyValue value={formatCurrency(totalLiabilities)} />
                        </FieldRow>

                        <Typography sx={{ fontWeight: 700 }}>Amortizaciones de los Créditos Vigentes de Largo Plazo</Typography>
                        <Grid container spacing={2}>
                            {dataForm.values.longTermAmortizations.map((amount, idx) => (
                                <Grid size={2} key={idx}>
                                    <Typography variant="body2" sx={{ fontWeight: 700, opacity: 0.55 }}>
                                        Año {idx + 1}
                                    </Typography>
                                    <MoneyInput
                                        value={amount}
                                        onChange={(value) => {
                                            const next = [...dataForm.values.longTermAmortizations] as typeof dataForm.values.longTermAmortizations;
                                            next[idx] = value;
                                            dataForm.setFieldValue("longTermAmortizations", next);
                                        }}
                                        disabled={disabled}
                                    />
                                </Grid>
                            ))}
                        </Grid>

                        <Typography sx={{ fontWeight: 700 }}>Activos del Cliente</Typography>
                        <FieldRow label="Bienes Muebles">
                            <MoneyInput
                                value={dataForm.values.assets.movable}
                                onChange={(value) => dataForm.setFieldValue("assets.movable", value)}
                                disabled={disabled}
                            />
                        </FieldRow>
                        <FieldRow label="Bienes Inmuebles">
                            <MoneyInput
                                value={dataForm.values.assets.immovable}
                                onChange={(value) => dataForm.setFieldValue("assets.immovable", value)}
                                disabled={disabled}
                            />
                        </FieldRow>
                        <FieldRow label="Total Activo Fijo">
                            <ReadonlyValue value={formatCurrency(totalFixedAssets)} />
                        </FieldRow>

                        <FieldRow label="Evaluador">
                            <ReadonlyValue value={evaluatorName} />
                        </FieldRow>
                        <FieldRow label="Promotor">
                            <TextInput
                                value={dataForm.values.promoter}
                                onChange={(value) => dataForm.setFieldValue("promoter", value)}
                                disabled={disabled}
                            />
                        </FieldRow>
                    </Stack>
                </CustomAccordion>
            </Paper>

            <Button
                variant="contained"
                sx={{ alignSelf: "flex-end" }}
                disabled={disabled || !dataForm.dirty}
                onClick={() => save()}
            >
                Guardar
            </Button>
        </Stack>
    );
}
